import { countries } from './countries';

const PHONE_CODES_DESC = [...new Set(countries.map((c) => c.phoneCode))].sort(
  (a, b) => b.length - a.length
);

export const DEFAULT_CONTACT_PHONE_COUNTRY_CODE = '+91';

/** Local number only — digits, spaces, dashes, parens (no leading +). */
export function normalizeContactPhoneLocal(value: unknown): string {
  if (value == null) return '';
  return String(value).replace(/[^\d\s().-]/g, '').trim();
}

/** Split a stored/full phone into country code + local number. */
export function splitContactPhone(value: unknown): { countryCode: string; localNumber: string } {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) {
    return { countryCode: DEFAULT_CONTACT_PHONE_COUNTRY_CODE, localNumber: '' };
  }

  const compact = raw.replace(/[\s().-]/g, '');

  if (compact.startsWith('+')) {
    for (const code of PHONE_CODES_DESC) {
      if (compact.startsWith(code)) {
        const rest = compact.slice(code.length);
        return {
          countryCode: code,
          localNumber: normalizeContactPhoneLocal(rest),
        };
      }
    }
  }

  return {
    countryCode: DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
    localNumber: normalizeContactPhoneLocal(raw),
  };
}

/** Combine country code + local number for API/storage. */
export function formatContactPhone(countryCode: string, localNumber: string): string {
  const local = normalizeContactPhoneLocal(localNumber);
  if (!local) return '';
  const code = countryCode.trim() || DEFAULT_CONTACT_PHONE_COUNTRY_CODE;
  return `${code} ${local}`.trim();
}

/** Single-line display for CRM/lists — never repeats country code. */
export function displayContactPhoneLine(countryCode?: string, phone?: string): string {
  const raw = (phone || '').trim();
  if (!raw && !countryCode?.trim()) return '';

  if (raw.startsWith('+')) {
    const split = splitContactPhone(raw);
    return formatContactPhone(split.countryCode, split.localNumber);
  }

  const code = (countryCode || DEFAULT_CONTACT_PHONE_COUNTRY_CODE).trim();
  let local = normalizeContactPhoneLocal(raw);
  if (!local) return code;

  const codeDigits = code.replace(/\D/g, '');
  const localDigits = local.replace(/\D/g, '');
  if (codeDigits && localDigits.startsWith(codeDigits) && localDigits.length > codeDigits.length) {
    local = normalizeContactPhoneLocal(localDigits.slice(codeDigits.length));
  }

  return formatContactPhone(code, local);
}

/** Hydrate form fields from saved draft or metadata. */
export function hydrateContactPhoneFields(saved: Record<string, unknown> | undefined): {
  contactPhoneCountryCode: string;
  contactPhone: string;
} {
  if (saved?.contactPhoneCountryCode != null && saved?.contactPhone != null) {
    return {
      contactPhoneCountryCode: String(saved.contactPhoneCountryCode),
      contactPhone: normalizeContactPhoneLocal(saved.contactPhone),
    };
  }

  const split = splitContactPhone(saved?.contactPhone);
  return {
    contactPhoneCountryCode: split.countryCode,
    contactPhone: split.localNumber,
  };
}

export function applyContactPhoneAutofill(
  next: Record<string, unknown>,
  value: unknown
): void {
  const split = splitContactPhone(value);
  next.contactPhoneCountryCode = split.countryCode;
  next.contactPhone = split.localNumber;
}
